// Client-side password hashing utility
// Note: This is additional security on top of HTTPS and server-side bcrypt

export async function hashPassword(password: string): Promise<string> {
  // For now, we'll return the password as-is since:
  // 1. HTTPS encrypts all traffic in production
  // 2. Server-side bcrypt provides the actual security
  // 3. Client-side hashing can give false sense of security
  
  // If you want to implement client-side hashing, you could:
  // 1. Use Web Crypto API for PBKDF2
  // 2. Use a library like bcryptjs
  // 3. Use SHA-256 (less secure but simple)
  
  // Example with Web Crypto API (uncomment to use):
  /*
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hash));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
  */
  
  return password;
}

// Audio encryption constants
const ENCRYPTION_ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96 bits for GCM
const SALT_LENGTH = 16; // 128 bits
const PBKDF2_ITERATIONS = 100000;

// Export for use in other modules
export const AUDIO_ENCRYPTION_CONFIG = {
  algorithm: ENCRYPTION_ALGORITHM,
  keyLength: KEY_LENGTH,
  ivLength: IV_LENGTH,
  tagLength: 128 // GCM auth tag
};

/**
 * Generate a cryptographically secure encryption key
 */
export async function generateEncryptionKey(): Promise<CryptoKey> {
  try {
    return await crypto.subtle.generateKey(
      {
        name: ENCRYPTION_ALGORITHM,
        length: KEY_LENGTH
      },
      true, // extractable
      ['encrypt', 'decrypt']
    );
  } catch (error) {
    console.error('Failed to generate encryption key:', error);
    throw new Error('Encryption key generation failed');
  }
}

/**
 * Export key to storable format
 */
export async function exportKey(key: CryptoKey): Promise<string> {
  try {
    const exported = await crypto.subtle.exportKey('raw', key);
    const keyArray = new Uint8Array(exported);
    // Convert to base64 using Buffer-like approach
    let binary = '';
    for (let i = 0; i < keyArray.byteLength; i++) {
      binary += String.fromCharCode(keyArray[i]);
    }
    return btoa(binary);
  } catch (error) {
    console.error('Failed to export key:', error);
    throw new Error('Key export failed');
  }
}

/**
 * Import key from stored format
 */
export async function importKey(keyString: string): Promise<CryptoKey> {
  try {
    const keyData = Uint8Array.from(atob(keyString), c => c.charCodeAt(0));
    return await crypto.subtle.importKey(
      'raw',
      keyData,
      ENCRYPTION_ALGORITHM,
      true,
      ['encrypt', 'decrypt']
    );
  } catch (error) {
    console.error('Failed to import key:', error);
    throw new Error('Key import failed');
  }
}

/**
 * Encrypt an audio blob
 * @param audioBlob - The audio data to encrypt
 * @param key - The encryption key
 * @returns Object containing encrypted data and metadata
 */
export async function encryptAudioBlob(
  audioBlob: Blob,
  key: CryptoKey
): Promise<{
  encryptedData: ArrayBuffer;
  iv: string;
  authTag: string;
  originalSize: number;
  mimeType: string;
}> {
  try {
    // Generate random IV
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    
    // Convert blob to ArrayBuffer
    const arrayBuffer = await audioBlob.arrayBuffer();
    
    // Encrypt the data
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: ENCRYPTION_ALGORITHM,
        iv: iv
      },
      key,
      arrayBuffer
    );
    
    // GCM mode includes auth tag in the last 16 bytes
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const authTag = encryptedArray.slice(-16);
    const encryptedData = encryptedArray.slice(0, -16);
    
    return {
      encryptedData: encryptedData.buffer,
      iv: btoa(Array.from(iv).map(b => String.fromCharCode(b)).join('')),
      authTag: btoa(Array.from(authTag).map(b => String.fromCharCode(b)).join('')),
      originalSize: audioBlob.size,
      mimeType: audioBlob.type
    };
  } catch (error) {
    console.error('Audio encryption failed:', error);
    throw new Error('Failed to encrypt audio data');
  }
}

/**
 * Decrypt audio data
 * @param encryptedData - The encrypted audio data
 * @param key - The decryption key
 * @param iv - The initialization vector
 * @param authTag - The authentication tag
 * @param mimeType - Original MIME type
 * @returns Decrypted audio blob
 */
export async function decryptAudioBlob(
  encryptedData: ArrayBuffer,
  key: CryptoKey,
  iv: string,
  authTag: string,
  mimeType: string
): Promise<Blob> {
  try {
    // Decode IV and auth tag
    const ivArray = Uint8Array.from(atob(iv), c => c.charCodeAt(0));
    const authTagArray = Uint8Array.from(atob(authTag), c => c.charCodeAt(0));
    
    // Combine encrypted data with auth tag (GCM expects them together)
    const combinedData = new Uint8Array(encryptedData.byteLength + authTagArray.length);
    combinedData.set(new Uint8Array(encryptedData), 0);
    combinedData.set(authTagArray, encryptedData.byteLength);
    
    // Decrypt
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: ENCRYPTION_ALGORITHM,
        iv: ivArray
      },
      key,
      combinedData.buffer
    );
    
    // Create blob with original MIME type
    return new Blob([decryptedBuffer], { type: mimeType });
  } catch (error) {
    console.error('Audio decryption failed:', error);
    throw new Error('Failed to decrypt audio data');
  }
}

/**
 * Derive a session key from user credentials and organization key
 * This provides an additional layer of security
 */
export async function deriveSessionKey(
  organizationKey: string,
  userId: string,
  sessionId: string
): Promise<CryptoKey> {
  try {
    // Create a unique salt for this session
    const encoder = new TextEncoder();
    const saltData = encoder.encode(`${organizationKey}:${userId}:${sessionId}`);
    const salt = await crypto.subtle.digest('SHA-256', saltData);
    
    // Import organization key as base key
    const baseKey = await crypto.subtle.importKey(
      'raw',
      encoder.encode(organizationKey),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );
    
    // Derive session key
    return await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: new Uint8Array(salt),
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256'
      },
      baseKey,
      {
        name: ENCRYPTION_ALGORITHM,
        length: KEY_LENGTH
      },
      true,
      ['encrypt', 'decrypt']
    );
  } catch (error) {
    console.error('Session key derivation failed:', error);
    throw new Error('Failed to derive session key');
  }
}

/**
 * Generate SHA-256 checksum for data integrity verification
 */
export async function generateChecksum(data: ArrayBuffer): Promise<string> {
  try {
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Checksum generation failed:', error);
    throw new Error('Failed to generate checksum');
  }
}

/**
 * Verify data integrity using checksum
 */
export async function verifyChecksum(data: ArrayBuffer, expectedChecksum: string): Promise<boolean> {
  try {
    const actualChecksum = await generateChecksum(data);
    return actualChecksum === expectedChecksum;
  } catch (error) {
    console.error('Checksum verification failed:', error);
    return false;
  }
}

// For production, ensure all API calls use HTTPS
export function ensureSecureConnection(): boolean {
  if (window.location.protocol !== 'https:' && 
      window.location.hostname !== 'localhost' && 
      window.location.hostname !== '127.0.0.1') {
    console.warn('⚠️ Insecure connection detected. Please use HTTPS in production.');
    return false;
  }
  return true;
}